






























































import { Component, Prop, Vue } from "vue-property-decorator";
import { PAGE_SIZE, pageCount } from "@/utility/pagination";

const GAP = -1;

/**
 * Page controls for a table. Presentational: the parent owns `page` and does
 * the paging, which is what lets one control serve both a list the client holds
 * in full (quotes, customers — sliced in the browser) and one it pages from the
 * API (sales orders, too many to fetch whole).
 *
 * Emits `change` with the requested page and scrolls the table back into view,
 * since the controls sit under 50 rows.
 */
@Component({
  filters: {
    number: (value: number): string => value.toLocaleString()
  }
})
export default class Pagination extends Vue {
  /** 1-based. */
  @Prop({ required: true }) page!: number;
  @Prop({ required: true }) total!: number;
  @Prop({ default: PAGE_SIZE }) perPage!: number;
  /** A server-paged parent is fetching; hold the controls until it lands. */
  @Prop({ default: false }) loading!: boolean;

  protected gap = GAP;

  protected get pages(): number {
    return pageCount(this.total, this.perPage);
  }

  protected get firstRow(): number {
    return (this.page - 1) * this.perPage + 1;
  }

  protected get lastRow(): number {
    return Math.min(this.page * this.perPage, this.total);
  }

  /**
   * First, last, and the current page with one neighbour each side; runs of
   * skipped pages collapse to a gap. A lone skipped page is shown rather than
   * gapped, since "…" would take the same space as the number it hides.
   */
  protected get items(): number[] {
    const wanted = new Set<number>([1, this.pages]);
    for (let p = this.page - 1; p <= this.page + 1; p++) {
      if (p >= 1 && p <= this.pages) wanted.add(p);
    }
    const sorted = Array.from(wanted).sort((a, b) => a - b);
    const items: number[] = [];
    sorted.forEach((p, i) => {
      const previous = sorted[i - 1];
      if (previous !== undefined && p - previous === 2) {
        items.push(p - 1);
      } else if (previous !== undefined && p - previous > 2) {
        items.push(GAP);
      }
      items.push(p);
    });
    return items;
  }

  protected go(target: number): void {
    if (target < 1 || target > this.pages || target === this.page) return;
    this.$emit("change", target);
    const table = this.$el.parentElement;
    if (table) {
      table.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
}
